import React, { useMemo, useRef, useState } from "react";

const SCORE_CAP = 130;
const BAR_MAX_PX = 250;

// --- MOBILE sizing knobs ---
const MOBILE_BAR_MAX = 230;
const MOBILE_CHART_H = MOBILE_BAR_MAX + 100;

// unified color tokens
const COLOR = {
  max: { fill: "bg-rose-300/80", dot: "bg-rose-400/90", text: "text-rose-500", border: "border-rose-200" },
  avg: { fill: "bg-blue-500",     dot: "bg-blue-500",     text: "text-blue-600", border: "border-blue-200" },
  min: { fill: "bg-emerald-500",  dot: "bg-emerald-500",  text: "text-emerald-600", border: "border-emerald-200" },
  line: "bg-slate-300/90",
};

const hNum = (v, maxPx = BAR_MAX_PX) =>
  (Math.max(0, Math.min(v, SCORE_CAP)) / SCORE_CAP) * maxPx;
const hPx = (v, maxPx = BAR_MAX_PX) => `${hNum(v, maxPx)}px`;

/* ----------------------- helpers ----------------------- */
function spaced(bottoms, gap) {
  const out = [...bottoms];
  for (let i = 1; i < out.length; i++) {
    if (out[i] < out[i - 1] + gap) out[i] = out[i - 1] + gap;
  }
  return out;
}

function bubblesFromHeights(minH, avgH, maxH, base, gap) {
  const [bMin, bAvg, bMax] = spaced([minH + base, avgH + base, maxH + base], gap);
  return { bMin, bAvg, bMax };
}

function ValueBubble({ value, bottomPx, tone = "gray", className = "" }) {
  const colorMap = {
    max: `${COLOR.max.text} ${COLOR.max.border}`,
    avg: `${COLOR.avg.text} ${COLOR.avg.border}`,
    min: `${COLOR.min.text} ${COLOR.min.border}`,
    gray: "text-gray-800 border-gray-200",
  };
  return (
    <div
      className={`absolute left-1/2 -translate-x-1/2 select-none rounded-xl bg-white font-semibold shadow-md px-2.5 py-1 border text-[10px] md:text-xs pointer-events-none ${colorMap[tone] || colorMap.gray} ${className}`}
      style={{ bottom: `${bottomPx}px` }}
    >
      {Number.isFinite(value) ? value.toFixed(1) : "—"}
    </div>
  );
}

const Dot = ({ className }) => (
  <span className={`inline-block w-2.5 h-2.5 rounded ${className}`} />
);

/* shared legend (Bar style) – used by BOTH modes */
const Legend = () => (
  <div className="mt-4 pb-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
    <div className="flex items-center gap-2">
      <span className={`inline-block w-3 h-3 rounded ${COLOR.max.fill}`} />
      <span>Maksimum</span>
    </div>
    <div className="flex items-center gap-2">
      <span className={`inline-block w-3 h-3 rounded ${COLOR.avg.fill}`} />
      <span>Rata-rata</span>
    </div>
    <div className="flex items-center gap-2">
      <span className={`inline-block w-3 h-3 rounded ${COLOR.min.fill}`} />
      <span>Minimum</span>
    </div>
  </div>
);

/* normalize mouse/touch coordinates */
function xyFromEvent(e) {
  const t = e?.touches?.[0] || e?.changedTouches?.[0] || e;
  return { clientX: t?.clientX ?? 0, clientY: t?.clientY ?? 0 };
}

/* -------------------------------- component ------------------------------- */
export default function MinMaxChart({ distribution = [], title = "Distribusi Performa Kelas" }) {
  const [mode, setMode] = useState("bar"); // 'bar' | 'range'

  // hover/tap overlay
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // { item, x, y }

  const handleEnter = (item, e) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const { clientX, clientY } = xyFromEvent(e);
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    setHover({ item, x, y });
  };
  const handleMove = (item, e) => handleEnter(item, e);
  const handleLeave = () => setHover(null);

  // tap/click toggles tooltip
  const handleTap = (item, e) => {
    e.stopPropagation();
    if (hover?.item?.code === item.code) {
      setHover(null);
      return;
    }
    handleEnter(item, e);
  };

  // close when clicking empty area
  const closeOnBackgroundClick = () => setHover(null);

  // constants for consistent placement + anti-overlap
  const DESKTOP_BASE = 10; // px above bar top
  const DESKTOP_GAP = 30; // min gap between min/avg/max labels
  const MOBILE_BASE = 8;
  const MOBILE_GAP = 30;

  // bubble spacing for DESKTOP
  const desktopBars = useMemo(
    () =>
      distribution.map((it) => {
        const maxH = hNum(it.max, BAR_MAX_PX);
        const avgH = hNum(it.average, BAR_MAX_PX);
        const minH = hNum(it.min, BAR_MAX_PX);
        const { bMin, bAvg, bMax } = bubblesFromHeights(
          minH,
          avgH,
          maxH,
          DESKTOP_BASE,
          DESKTOP_GAP
        );
        return { it, maxH, avgH, minH, bMin, bAvg, bMax };
      }),
    [distribution]
  );

  // bubble spacing for MOBILE
  const mobileBars = useMemo(
    () =>
      distribution.map((it) => {
        const maxH = hNum(it.max, MOBILE_BAR_MAX);
        const avgH = hNum(it.average, MOBILE_BAR_MAX);
        const minH = hNum(it.min, MOBILE_BAR_MAX);
        const { bMin, bAvg, bMax } = bubblesFromHeights(
          minH,
          avgH,
          maxH,
          MOBILE_BASE,
          MOBILE_GAP
        );
        return { it, maxH, avgH, minH, bMin, bAvg, bMax };
      }),
    [distribution]
  );

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 overflow-visible">
      {/* header */}
      <div className="px-5 md:px-8 py-4 md:py-6 border-b border-gray-200/50 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <svg
              className="w-5 h-5 md:w-6 md:h-6 text-blue-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 19h4V9H4v10zm6 0h4V5h-4v14zm6 0h4V13h-4v6z"
              />
            </svg>
          </div>
          <span>{title}</span>
        </h3>

        <div className="inline-flex rounded-full bg-slate-100 p-1">
          <button
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              mode === "bar"
                ? "bg-white shadow text-slate-900"
                : "text-slate-600 hover:text-slate-900"
            }`}
            onClick={() => setMode("bar")}
          >
            Bar
          </button>
          <button
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              mode === "range"
                ? "bg-white shadow text-slate-900"
                : "text-slate-600 hover:text-slate-900"
            }`}
            onClick={() => setMode("range")}
          >
            Range
          </button>
        </div>
      </div>

      {/* CHART AREA */}
      <div
        ref={wrapRef}
        className="relative px-4 md:p-8"
        onClick={closeOnBackgroundClick}
        onTouchEndCapture={closeOnBackgroundClick}
      >
        {!distribution.length ? (
          <div className="text-gray-500 p-6">No data</div>
        ) : mode === "bar" ? (
          <>
            {/* Mobile – only the chart scroller scrolls (legend is outside) */}
            <div className="md:hidden">
              <div
                className="overflow-x-auto scroll-smooth pb-2"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <div
                  className="pt-4 flex items-end gap-5 min-w-max pr-4 overflow-visible"
                  style={{ height: MOBILE_CHART_H }}
                >
                  {mobileBars.map(({ it, bMin, bAvg, bMax }) => (
                    <div
                      key={it.code}
                      className="flex flex-col items-center relative px-1"
                      onMouseEnter={(e) => handleEnter(it, e)}
                      onMouseMove={(e) => handleMove(it, e)}
                      onMouseLeave={handleLeave}
                      onTouchStart={(e) => handleTap(it, e)}
                      onClick={(e) => handleTap(it, e)}
                    >
                      <div className="relative w-16 flex items-end justify-center">
                        {/* bars */}
                        <div
                          className={`absolute bottom-0 w-10 rounded-t-2xl ${COLOR.max.fill} shadow z-0`}
                          style={{ height: hPx(it.max, MOBILE_BAR_MAX) }}
                        />
                        <div
                          className={`absolute bottom-0 w-8  rounded-t-2xl ${COLOR.avg.fill} shadow z-0`}
                          style={{ height: hPx(it.average, MOBILE_BAR_MAX) }}
                        />
                        <div
                          className={`absolute bottom-0 w-6  rounded-t-2xl ${COLOR.min.fill} shadow z-0`}
                          style={{ height: hPx(it.min, MOBILE_BAR_MAX) }}
                        />

                        <ValueBubble value={it.min} bottomPx={bMin} tone="min" className="z-20" />
                        <ValueBubble value={it.average} bottomPx={bAvg} tone="avg" className="z-20" />
                        <ValueBubble value={it.max} bottomPx={bMax} tone="max" className="z-20" />
                      </div>
                      <div className="mt-2 text-center">
                        <div className="text-xs font-semibold text-gray-700">{it.code}</div>
                        <div className="text-[10px] text-gray-500">{it.average.toFixed(1)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Desktop — centered, scrollable, no spill */}
            <div className="hidden md:block">
              <div className="overflow-x-auto">
                <div className="h-96 flex items-end justify-center gap-12 min-w-max mx-auto px-6 overflow-visible">
                  {desktopBars.map(({ it, bMin, bAvg, bMax }) => (
                    <div
                      key={it.code}
                      className="flex flex-col items-center group relative"
                      onMouseEnter={(e) => handleEnter(it, e)}
                      onMouseMove={(e) => handleMove(it, e)}
                      onMouseLeave={handleLeave}
                      onClick={(e) => handleTap(it, e)}
                    >
                      <div className="relative w-24 flex items-end justify-center">
                        {/* bars */}
                        <div
                          className={`absolute bottom-0 w-16 rounded-t-2xl ${COLOR.max.fill} shadow-lg z-0`}
                          style={{ height: hPx(it.max) }}
                        />
                        <div
                          className={`absolute bottom-0 w-12 rounded-t-2xl ${COLOR.avg.fill} shadow-lg z-0`}
                          style={{ height: hPx(it.average) }}
                        />
                        <div
                          className={`absolute bottom-0 w-8  rounded-t-2xl ${COLOR.min.fill} shadow-md z-0`}
                          style={{ height: hPx(it.min) }}
                        />

                        <ValueBubble value={it.min} bottomPx={bMin} tone="min" className="z-20" />
                        <ValueBubble value={it.average} bottomPx={bAvg} tone="avg" className="z-20" />
                        <ValueBubble value={it.max} bottomPx={bMax} tone="max" className="z-20" />
                      </div>
                      <div className="mt-4 text-center">
                        <div className="text-sm font-semibold text-gray-700">{it.code}</div>
                        <div className="text-xs text-gray-500">{it.average.toFixed(1)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          // RANGE MODE
          <>
            {/* Mobile */}
            <div className="md:hidden">
              <div
                className="overflow-x-auto pb-2"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <div
                  className="pt-6 flex items-end gap-10 min-w-max pr-4 overflow-visible"
                  style={{ height: MOBILE_CHART_H }}
                >
                  {distribution.map((it) => (
                    <div
                      key={it.code}
                      className="flex flex-col items-center relative"
                      onMouseEnter={(e) => handleEnter(it, e)}
                      onMouseMove={(e) => handleMove(it, e)}
                      onMouseLeave={handleLeave}
                      onTouchStart={(e) => handleTap(it, e)}
                      onClick={(e) => handleTap(it, e)}
                    >
                      <div
                        className="relative w-2 flex items-end justify-center"
                        style={{ height: MOBILE_BAR_MAX }}
                      >
                        <div
                          className={`absolute left-1/2 -translate-x-1/2 w-[2px] ${COLOR.line}`}
                          style={{
                            bottom: `${hNum(it.min, MOBILE_BAR_MAX)}px`,
                            height: `${
                              hNum(it.max, MOBILE_BAR_MAX) - hNum(it.min, MOBILE_BAR_MAX)
                            }px`,
                          }}
                        />
                        <div
                          className={`absolute rounded-sm w-3 h-3 ${COLOR.max.dot} translate-y-1/2`}
                          style={{ bottom: hNum(it.max, MOBILE_BAR_MAX) }}
                        />
                        <div
                          className={`absolute rounded-sm w-3 h-3 ${COLOR.avg.dot} translate-y-1/2`}
                          style={{ bottom: hNum(it.average, MOBILE_BAR_MAX) }}
                        />
                        <div
                          className={`absolute rounded-sm w-3 h-3 ${COLOR.min.dot} translate-y-1/2`}
                          style={{ bottom: hNum(it.min, MOBILE_BAR_MAX) }}
                        />
                      </div>
                      <div className="mt-2 text-center">
                        <div className="text-xs font-semibold text-gray-700">{it.code}</div>
                        <div className="text-[10px] text-gray-500">
                          {it.average.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Desktop — centered, scrollable, no spill */}
            <div className="hidden md:block">
              <div className="overflow-x-auto">
                <div className="h-96 flex items-end justify-center gap-20 min-w-max mx-auto px-6">
                  {distribution.map((it) => (
                    <div
                      key={it.code}
                      className="flex flex-col items-center"
                      onMouseEnter={(e) => handleEnter(it, e)}
                      onMouseMove={(e) => handleMove(it, e)}
                      onMouseLeave={handleLeave}
                      onClick={(e) => handleTap(it, e)}
                    >
                      <div className="relative w-4 h-[250px] flex items-end justify-center">
                        <div
                          className={`absolute left-1/2 -translate-x-1/2 w-[2px] ${COLOR.line}`}
                          style={{
                            bottom: `${hNum(it.min)}px`,
                            height: `${hNum(it.max) - hNum(it.min)}px`,
                          }}
                        />
                        <div
                          className={`absolute w-3.5 h-3.5 rounded-sm ${COLOR.max.dot} translate-y-1/2`}
                          style={{ bottom: hNum(it.max) }}
                          title={`Max ${it.max.toFixed(1)}`}
                        />
                        <div
                          className={`absolute w-3.5 h-3.5 rounded-sm ${COLOR.avg.dot} translate-y-1/2`}
                          style={{ bottom: hNum(it.average) }}
                          title={`Avg ${it.average.toFixed(1)}`}
                        />
                        <div
                          className={`absolute w-3.5 h-3.5 rounded-sm ${COLOR.min.dot} translate-y-1/2`}
                          style={{ bottom: hNum(it.min) }}
                          title={`Min ${it.min.toFixed(1)}`}
                        />
                      </div>
                      <div className="mt-4 text-center">
                        <div className="text-sm font-semibold text-gray-700">{it.code}</div>
                        <div className="text-xs text-gray-500">
                          {it.average.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ONE shared legend for both modes (Bar style) */}
        <Legend />

        {/* Hover/tap overlay */}
        {hover?.item && (
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              left: Math.max(
                8,
                Math.min(hover.x + 14, (wrapRef.current?.clientWidth || 0) - 8)
              ),
              top: Math.max(8, hover.y - 10),
            }}
          >
            <div className="bg-white/95 backdrop-blur shadow-2xl rounded-xl border border-slate-200 p-3 text-xs text-slate-800">
              <div className="font-semibold text-slate-900 mb-1">
                {hover.item.name} ({hover.item.code})
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Dot className={COLOR.max.dot} />
                  <span className="text-slate-600">Maksimum:</span>
                  <span className="tabular-nums font-semibold">
                    {hover.item.max.toFixed(1)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Dot className={COLOR.avg.dot} />
                  <span className="text-slate-600">Rata-rata:</span>
                  <span className="tabular-nums font-semibold">
                    {hover.item.average.toFixed(1)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Dot className={COLOR.min.dot} />
                  <span className="text-slate-600">Minimum:</span>
                  <span className="tabular-nums font-semibold">
                    {hover.item.min.toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
